import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {NgClass} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {Headline, ScoreCardComponent, Subheadline} from '@spirit-testing/component-library';
import {MatButton} from '@angular/material/button';
import {MatCard, MatCardHeader, MatCardSubtitle, MatCardTitle} from '@angular/material/card';
import {MessageService} from '../../shared/components/message.service';
import {TranslatePipe} from '@ngx-translate/core';
import {MatDivider} from '@angular/material/list';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {AnalyzeResultComponent} from './analyze-result/analyze-result.component';
import {ImproveResultComponent} from './improve-result/improve-result.component';
import {AiProfileMatchingService} from './ai-profile-matching.service';
import {AiAnalysis, AiExportResponse, AiImproveSummary, AiDiffHunk, Language} from './ai-profile-matching.model';

type AnalyzeState = 'idle' | 'processing' | 'complete' | 'error';
type ImproveState = 'idle' | 'processing' | 'complete' | 'error';
import {MatFormField, MatLabel} from '@angular/material/input';
import {MatOption, MatSelect} from '@angular/material/select';
import {MatIcon} from '@angular/material/icon';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatInput} from '@angular/material/input';
import {CdkTextareaAutosize} from '@angular/cdk/text-field';

@Component({
  selector: 'app-ai-profile-matching',
  imports: [
    Headline,
    Subheadline,
    MatButton,
    TranslatePipe,
    ScoreCardComponent,
    MatDivider,
    MatProgressSpinner,
    MatCard,
    MatCardHeader,
    MatCardSubtitle,
    MatCardTitle,
    AnalyzeResultComponent,
    ImproveResultComponent,
    NgClass,
    FormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatIcon,
    MatExpansionModule,
    MatInput,
    CdkTextareaAutosize
  ],
  templateUrl: './ai-profile-matching.component.html',
  styleUrl: './ai-profile-matching.component.scss'
})
export class AiProfileMatchingComponent implements OnInit {
  @ViewChild('jobFileInput') jobFileInput!: ElementRef<HTMLInputElement>;

  consultantId: string | null = null;
  jobData: string | null = null;
  jobFileName: string | null = null;

  analyzeState: AnalyzeState = 'idle';
  improveState: ImproveState = 'idle';
  isLoading: boolean = false;
  loadError: boolean = false;
  analysis: AiAnalysis | null = null;
  improvedProfile: string | null = null;
  hunks: AiDiffHunk[] | null = null;
  improveSummary: AiImproveSummary | null = null;
  aiExport: AiExportResponse | null = null;
  isApplyingDecisions: boolean = false;
  appliedProfile: string | null = null;
  selectedLanguage: Language = Language.DE;

  prompts: Record<string, string> = {};
  promptOverrides: Record<string, string> = {};
  selectedPromptKey: string | null = null;
  promptKeys: string[] = [];

  readonly languages = [
    {value: Language.DE, label: 'appComponent.languageMenu.german'},
    {value: Language.EN, label: 'appComponent.languageMenu.english'}
  ];

  constructor(
    private route: ActivatedRoute,
    private messageService: MessageService,
    private aiService: AiProfileMatchingService
  ) {
  }

  ngOnInit() {
    this.consultantId = this.route.snapshot.paramMap.get('consultantId');
    this.loadProfile();
    this.loadPrompts();
  }

  loadPrompts() {
    this.aiService.getPrompts().subscribe({
      next: (response) => {
        this.prompts = response.prompts;
        this.promptKeys = Object.keys(response.prompts);
        this.promptOverrides = {...response.prompts};
      },
      error: () => {
        this.messageService.warning('aiProfileMatching.errorMessages.promptsLoadFailed');
      }
    });
  }

  onPromptSelected(key: string) {
    this.selectedPromptKey = key;
  }

  isPromptModified(key: string): boolean {
    return this.promptOverrides[key] !== this.prompts[key];
  }

  resetPrompt(key: string) {
    this.promptOverrides[key] = this.prompts[key];
  }

  resetAllPrompts() {
    this.promptOverrides = {...this.prompts};
  }

  jobDescriptionButton() {
    this.jobFileInput.nativeElement.click();
  }

  loadProfile() {
    if (!this.consultantId) return;

    this.isLoading = true;
    this.loadError = false;
    this.aiExport = null;

    this.aiService.getAiExport(this.consultantId).subscribe({
      next: (data) => {
        this.aiExport = data;
        this.isLoading = false;
      },
      error: () => {
        this.loadError = true;
        this.isLoading = false;
        this.messageService.error('aiProfileMatching.errorMessages.loadFailed');
      }
    });
  }

  startAnalyze() {
    if (!this.aiExport?.sanitized || !this.jobData) {
      this.messageService.error('aiProfileMatching.errorMessages.uploadFiles');
      return;
    }

    this.analyzeState = 'processing';
    this.improveState = 'idle';
    this.analysis = null;
    this.improvedProfile = null;
    this.hunks = null;
    this.improveSummary = null;
    this.appliedProfile = null;
    this.isApplyingDecisions = false;

    const changedOverrides: Record<string, string> = {};
    for (const key of this.promptKeys) {
      if (this.promptOverrides[key] !== this.prompts[key]) {
        changedOverrides[key] = this.promptOverrides[key];
      }
    }
    const overrides = Object.keys(changedOverrides).length > 0 ? changedOverrides : undefined;

    this.aiService.analyze(this.consultantId!, this.aiExport.sanitized, this.jobData, this.selectedLanguage, overrides).subscribe({
      next: (response) => {
        this.analysis = response.analysis;
        this.analyzeState = 'complete';
      },
      error: () => {
        this.messageService.error('aiProfileMatching.errorMessages.analysisFailed');
        this.analyzeState = 'error';
      }
    });
  }

  startImprove() {
    if (!this.analysis || !this.aiExport?.sanitized || !this.jobData) return;

    this.improveState = 'processing';
    this.improvedProfile = null;
    this.hunks = null;
    this.improveSummary = null;
    this.appliedProfile = null;
    this.isApplyingDecisions = false;

    const changedOverrides: Record<string, string> = {};
    for (const key of this.promptKeys) {
      if (this.promptOverrides[key] !== this.prompts[key]) {
        changedOverrides[key] = this.promptOverrides[key];
      }
    }
    const overrides = Object.keys(changedOverrides).length > 0 ? changedOverrides : undefined;

    this.aiService.improve(
      this.consultantId!, this.aiExport.sanitized, this.jobData,
      this.analysis, this.selectedLanguage, overrides
    ).subscribe({
      next: (response) => {
        this.improvedProfile = response.improved_profile;
        this.hunks = response.hunks;
        this.improveSummary = response.summary;
        this.improveState = 'complete';
      },
      error: () => {
        this.messageService.error('aiProfileMatching.errorMessages.improveFailed');
        this.improveState = 'error';
      }
    });
  }

  onJobFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    const fileName = file.name.toLowerCase();

    if (!fileName.endsWith('.json') && !fileName.endsWith('.txt')) {
      this.messageService.warning('aiProfileMatching.errorMessages.formatting');
      input.value = '';
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      this.jobData = reader.result as string;
      this.jobFileName = file.name;
      input.value = '';
    };

    reader.readAsText(file);
  }


  onApplyDecisions(decisions: Record<string, boolean>): void {
    if (!this.aiExport?.sanitized || !this.improvedProfile) return;

    this.isApplyingDecisions = true;
    this.aiService.applyDecisions(this.consultantId!, this.aiExport.sanitized, this.improvedProfile, decisions).subscribe({
      next: (response) => {
        this.appliedProfile = response.finalProfile;
        this.isApplyingDecisions = false;
        this.messageService.success('aiProfileMatching.applySuccess');
      },
      error: () => {
        this.messageService.error('aiProfileMatching.errorMessages.applyFailed');
        this.isApplyingDecisions = false;
      }
    });
  }

  removeJobFile(event: Event) {
    event.stopPropagation();
    this.jobFileName = null;

    const fileInput = document.querySelector<HTMLInputElement>('#jobFileInput');
    if (fileInput) fileInput.value = '';
  }
}
